// src/pages/admin/AdminManagement.jsx
import { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  subscribeAllAdmins, createAdminRecord,
  updateAdminRole, toggleAdminActive, deleteAdminRecord,
  createRepresentativeRecord, updateRepresentative,
  ROLE_LABELS, ROLE_COLORS,
} from "../../firebase/auth";
import { logActivity } from "../../firebase/auth";
import { B, fonts, Input, Btn, Card, Toast, Spinner, useConfirmModal } from "../../components/UI";

export default function AdminManagement({ onSavingChange }) {
  const { profile }   = useAuth();
  const [admins,  setAdmins]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showRepForm, setShowRepForm] = useState(false);
  const { confirm, confirmModal } = useConfirmModal();

  useEffect(() => {
    const unsub = subscribeAllAdmins(a => { setAdmins(a); setLoading(false); });
    return unsub;
  }, []);

  const showToast = (msg, color = B.orange) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 3200);
  };

  const handleRoleChange = async (uid, role) => {
    await updateAdminRole(uid, role, profile.uid);
    showToast("Role updated");
  };

  const handleToggle = async (uid, active) => {
    await toggleAdminActive(uid, !active, profile.uid);
    showToast(!active ? "Admin enabled" : "Admin disabled", !active ? B.green : B.red);
  };

  const handleDelete = async (uid, email, name, role) => {
    const isRep = role === "representative";
    const label = isRep ? (name || "representative") : email;
    const confirmed = await confirm({
      title: isRep ? "Remove representative?" : "Remove admin access?",
      message: isRep
        ? `${label} will no longer be assignable to video calls or receive WhatsApp reminders.`
        : `${email} will no longer be able to access the admin dashboard.`,
      confirmLabel: isRep ? "Remove Representative" : "Remove Admin",
    });
    if (!confirmed) return;
    await deleteAdminRecord(uid, profile.uid);
    showToast(isRep ? "Representative removed" : "Admin removed", B.red);
  };

  const handleRepPhoneChange = async (id, whatsapp_phone) => {
    if (!whatsapp_phone) return;
    await updateRepresentative(id, { whatsapp_phone }, profile.uid);
    showToast("WhatsApp number updated");
  };

  return (
    <div>
      <Toast toast={toast} />
      {confirmModal}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, gap: 10, flexWrap: "wrap" }}>
        <p style={{ margin: 0, fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>Manage who can access the admin dashboard, and representatives who receive video-call reminders</p>
        <div style={{ display: "flex", gap: 10 }}>
          {!showForm && <Btn onClick={() => setShowForm(true)} variant="primary">+ Add Admin</Btn>}
          {!showRepForm && <Btn onClick={() => setShowRepForm(true)} variant="secondary">+ Add Representative</Btn>}
        </div>
      </div>

      {showForm && (
        <AddAdminForm
          onDone={() => setShowForm(false)}
          showToast={showToast}
          createdByUid={profile.uid}
          onSavingChange={onSavingChange}
        />
      )}

      {showRepForm && (
        <AddRepresentativeForm
          onDone={() => setShowRepForm(false)}
          showToast={showToast}
          createdByUid={profile.uid}
          onSavingChange={onSavingChange}
        />
      )}

      {/* How to set up first admin note */}
      <div style={{ background: B.goldLight, border: `1px solid ${B.gold}44`, borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: B.gold, fontWeight: 700, marginBottom: 4, fontFamily: fonts.body }}>
          ℹ️ How to add a new admin
        </div>
        <p style={{ fontSize: 12, color: B.textMid, margin: 0, fontFamily: fonts.body, lineHeight: 1.6 }}>
          First create the user in <strong>Firebase Console → Authentication → Add user</strong> (email + password). Copy their UID, then use the form above to register them here with a role.
          For Google login users — they must sign in with Google once first, then you can find their UID in Firebase Console → Authentication → Users.
        </p>
      </div>

      {loading ? <Spinner /> : admins.length === 0
        ? <div style={{ textAlign: "center", padding: 48, color: B.textLight, fontFamily: fonts.body }}>No admins yet.</div>
        : admins.map(a => (
          <AdminRow key={a.uid} admin={a} currentUid={profile.uid}
            onRoleChange={handleRoleChange}
            onToggle={handleToggle}
            onDelete={handleDelete}
            onPhoneChange={handleRepPhoneChange}
          />
        ))
      }
    </div>
  );
}

// ─── Admin Row ────────────────────────────────────────────────────────────────
function AdminRow({ admin, currentUid, onRoleChange, onToggle, onDelete, onPhoneChange }) {
  const isYou = admin.uid === currentUid;
  const isRep = admin.role === "representative";

  const editRepPhone = () => {
    const p = typeof window !== "undefined" && typeof window.prompt === "function"
      ? window.prompt("WhatsApp number (with country code, e.g. 91)", admin.whatsapp_phone || "91")
      : "";
    const digits = (p || "").replace(/\D/g, "");
    if (digits) onPhoneChange(admin.uid, digits);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", background: admin.active ? B.white : "#FFF8F5", border: `1px solid ${admin.active ? B.borderLight : B.orangeMid}`, borderRadius: 12, marginBottom: 10, opacity: admin.active ? 1 : 0.65 }}>

      {/* Avatar */}
      <div style={{ width: 44, height: 44, borderRadius: "50%", background: `${ROLE_COLORS[admin.role]}22`, border: `2px solid ${ROLE_COLORS[admin.role]}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
        {admin.name?.[0]?.toUpperCase() || "?"}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: B.text, fontFamily: fonts.adminDisplay }}>{admin.name}</span>
          {isYou && <span style={{ fontSize: 10, background: B.orangeFaint, color: B.orange, padding: "1px 8px", borderRadius: 4, fontFamily: fonts.body, fontWeight: 600 }}>You</span>}
          <span style={{ fontSize: 11, background: `${ROLE_COLORS[admin.role]}18`, color: ROLE_COLORS[admin.role], border: `1px solid ${ROLE_COLORS[admin.role]}44`, padding: "1px 8px", borderRadius: 4, fontWeight: 700, fontFamily: fonts.body }}>
            {ROLE_LABELS[admin.role]}
          </span>
          {!admin.active && <span style={{ fontSize: 10, background: B.redLight, color: B.red, padding: "1px 8px", borderRadius: 4, fontFamily: fonts.body, fontWeight: 600 }}>Disabled</span>}
        </div>
        <div style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>
          {isRep
            ? (admin.whatsapp_phone ? `📱 WhatsApp: +${admin.whatsapp_phone}` : "No WhatsApp number set")
            : admin.email}
        </div>
      </div>

      {/* Actions — disabled for self */}
      {!isYou && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          {isRep ? (
            <Btn onClick={editRepPhone} variant="secondary" small title="Edit WhatsApp number">✏️ Edit #</Btn>
          ) : (
            <select value={admin.role} onChange={e => onRoleChange(admin.uid, e.target.value)}
              style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", cursor: "pointer" }}>
              <option value="super_admin">Super Admin</option>
              <option value="catalog_manager">Catalog Manager</option>
              <option value="viewer">Viewer</option>
            </select>
          )}
          <Btn onClick={() => onToggle(admin.uid, admin.active)} variant={admin.active ? "secondary" : "success"} small>
            {admin.active ? "Disable" : "Enable"}
          </Btn>
          <Btn onClick={() => onDelete(admin.uid, admin.email, admin.name, admin.role)} variant="danger" small>Remove</Btn>
        </div>
      )}
    </div>
  );
}

// ─── Add Admin Form ───────────────────────────────────────────────────────────
function AddAdminForm({ onDone, showToast, createdByUid, onSavingChange }) {
  const [form,   setForm]   = useState({ uid: "", name: "", email: "", role: "catalog_manager" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.uid || !form.name || !form.email) { showToast("UID, name and email are required", B.red); return; }
    setSaving(true);
    onSavingChange?.(true);
    try {
      await createAdminRecord(form.uid, { name: form.name, email: form.email, role: form.role }, createdByUid);
      showToast(`${form.name} added as ${ROLE_LABELS[form.role]}`);
      onDone();
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  return (
    <Card style={{ marginBottom: 24, border: `1.5px solid ${B.orangeMid}`, background: B.orangeFaint }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: B.text, marginBottom: 18, fontFamily: fonts.adminDisplay }}>➕ Add New Admin</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
        <Input label="Firebase UID" value={form.uid} onChange={e => set("uid", e.target.value)}
          placeholder="Get from Firebase Console → Auth → Users" />
        <Input label="Full Name"    value={form.name}  onChange={e => set("name", e.target.value)}  placeholder="e.g. Ravi Patel" />
        <Input label="Email"        value={form.email} onChange={e => set("email", e.target.value)} placeholder="e.g. ravi@gmail.com" type="email" />
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 6, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>Role</label>
          <select value={form.role} onChange={e => set("role", e.target.value)}
            style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 13px", fontSize: 13, color: B.text, fontFamily: fonts.body, outline: "none" }}>
            <option value="catalog_manager">Catalog Manager</option>
            <option value="viewer">Viewer</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn onClick={onDone} variant="ghost">Cancel</Btn>
        <Btn onClick={handleSave} disabled={saving} variant="primary">{saving ? "Adding..." : "Add Admin"}</Btn>
      </div>
    </Card>
  );
}

// ─── Add Representative Form ──────────────────────────────────────────────────
// Representatives are NOT Firebase Auth users — they are auto-id'd identities
// used only to assign video calls and receive WhatsApp reminders (no login, no
// dashboard access). Fields: display name + WhatsApp number.
function AddRepresentativeForm({ onDone, showToast, createdByUid, onSavingChange }) {
  const [form,  setForm]  = useState({ name: "", whatsapp_phone: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    const name = form.name.trim();
    const digits = form.whatsapp_phone.replace(/\D/g, "");
    if (!name) { showToast("Name is required", B.red); return; }
    if (!digits) { showToast("WhatsApp number is required", B.red); return; }
    setSaving(true);
    onSavingChange?.(true);
    try {
      await createRepresentativeRecord({ name, whatsapp_phone: digits }, createdByUid);
      showToast(`${name} added as Representative`);
      onDone();
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  return (
    <Card style={{ marginBottom: 24, border: `1.5px solid ${B.orangeMid}`, background: B.orangeFaint }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: B.text, marginBottom: 6, fontFamily: fonts.adminDisplay }}>➕ Add New Representative</div>
      <p style={{ margin: "0 0 18px", fontSize: 12, color: B.textMid, fontFamily: fonts.body, lineHeight: 1.5 }}>
        Representatives are internal staff who take video calls. They don't log in —
        they just receive a WhatsApp reminder before each assigned call.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
        <Input label="Full Name" value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Ravi Patel" />
        <Input label="WhatsApp Number" value={form.whatsapp_phone} onChange={e => set("whatsapp_phone", e.target.value)} placeholder="e.g. 919876543210" />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn onClick={onDone} variant="ghost">Cancel</Btn>
        <Btn onClick={handleSave} disabled={saving} variant="primary">{saving ? "Adding..." : "Add Representative"}</Btn>
      </div>
    </Card>
  );
}
